/**
 * @file Attendance.jsx
 * @description Enterprise-Grade Timesheet Matrix & Dynamic Attendance Terminal.
 * @module modules/leave-management/pages/Attendance
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../api/client';
import { leaveService } from '../services/leaveService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Box, Grid, Typography, Button, Stack, MenuItem,
    Select, FormControl, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, LinearProgress, ButtonGroup, Avatar,
    Chip, Tooltip
} from '@mui/material';
import {
    LogIn, LogOut, FileText, Download, RefreshCw, BarChart2,
    Grid3x3, HelpCircle, Bell, Calendar, AlertTriangle
} from 'lucide-react';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS_LIST = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const formatTodayIST = () => {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(new Date());
};

const Attendance = () => {
    const [attendanceLogs, setAttendanceLogs] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [liveTime, setLiveTime] = useState(new Date());

    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(currentYear);

    // Live Clock Sync
    useEffect(() => {
        const timer = setInterval(() => setLiveTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Generate month layout details safely
    const monthDays = useMemo(() => {
        const totalDays = new Date(selectedYear, selectedMonth + 1, 0).getDate();
        return Array.from({ length: totalDays }, (_, i) => {
            const day = i + 1;
            const formattedDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayOfWeek = new Date(selectedYear, selectedMonth, day).getDay();
            return {
                dayNum: day,
                dayName: DAYS_OF_WEEK[dayOfWeek],
                isCompanyWeekOff: dayOfWeek === 2, // Tuesday Rest Constant
                rawDateString: formattedDate
            };
        });
    }, [selectedMonth, selectedYear]);

    /**
     * FIX: Restrict the "isClockedIn" state to only apply to TODAY'S records.
     */
    const activeLog = attendanceLogs[0];
    const isClockedIn = useMemo(() => {
        if (!activeLog) return false;

        const todayStr = formatTodayIST();
        let logDateClean = activeLog?.date || '';
        if (logDateClean && logDateClean.includes('T')) {
            logDateClean = logDateClean.split('T')[0];
        }

        return logDateClean === todayStr && !activeLog.clock_out;
    }, [activeLog]);

    const attendanceMap = useMemo(() => {
        const map = {};
        const istFormatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });

        attendanceLogs.forEach(l => {
            if (!l.date) return;
            let cleanDbDate;
            try {
                cleanDbDate = istFormatter.format(new Date(l.date));
            } catch (e) {
                cleanDbDate = l.date.includes('T') ? l.date.split('T')[0] : l.date.trim().substring(0, 10);
            }

            let isLateIn = false;
            if (l.clock_in) {
                try {
                    const [hours, minutes] = l.clock_in.split(':').map(Number);
                    if (((hours * 60) + minutes) > 675) { // 11:15 AM
                        isLateIn = true;
                    }
                } catch (e) {
                    console.error("Time delta sync issue: ", e);
                }
            }
            map[cleanDbDate] = { log: l, isLateIn };
        });
        return map;
    }, [attendanceLogs]);

    const leavesMap = useMemo(() => {
        const map = {};
        leaves.forEach(l => {
            const status = String(l.manager_status || l.status || 'Pending').toLowerCase().trim();
            if (status !== 'approved') return;

            const startClean = String(l.start_date).split('T')[0];
            const endClean = String(l.end_date).split('T')[0];

            let current = new Date(`${startClean}T00:00:00`);
            const end = new Date(`${endClean}T00:00:00`);
            while (current <= end) {
                const dateStr = current.toISOString().split('T')[0];
                map[dateStr] = l;
                current.setDate(current.getDate() + 1);
            }
        });
        return map;
    }, [leaves]);

    const metricsSummary = useMemo(() => {
        let totalPunches = 0;
        let lateCount = 0;
        let approvedLeaveCount = 0;
        let totalWorkingDays = 0;
        const todayStr = formatTodayIST();

        monthDays.forEach(day => {
            if (!day.isCompanyWeekOff) {
                if (day.rawDateString <= todayStr) {
                    totalWorkingDays++;
                }
            }
            if (attendanceMap[day.rawDateString]) {
                totalPunches++;
                if (attendanceMap[day.rawDateString].isLateIn) lateCount++;
            }
            if (leavesMap[day.rawDateString]) {
                approvedLeaveCount++;
            }
        });

        const absentCount = Math.max(0, totalWorkingDays - totalPunches - approvedLeaveCount);
        const completionRate = totalWorkingDays > 0 ? Math.round(((totalPunches + approvedLeaveCount) / totalWorkingDays) * 100) : 0;

        return { totalPunches, lateCount, approvedLeaveCount, absentCount, totalWorkingDays, completionRate };
    }, [monthDays, attendanceMap, leavesMap]);

    const loadAttendanceTimeline = useCallback(async () => {
        setLoading(true);
        try {
            const [attendanceRes, leaveRes] = await Promise.allSettled([
                API.get(`/attendance/history`, { params: { month: selectedMonth + 1, year: selectedYear } }),
                leaveService.getMyLeaves()
            ]);
            if (attendanceRes.status === 'fulfilled') {
                setAttendanceLogs(attendanceRes.value?.data?.logs || []);
            }
            if (leaveRes.status === 'fulfilled') {
                const leaveData = leaveRes.value?.data?.data || leaveRes.value?.data || [];
                setLeaves(Array.isArray(leaveData) ? leaveData : []);
            }
        } catch (err) {
            toast.error("Worksheet structural synchronization failed.");
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, selectedYear]);

    useEffect(() => {
        loadAttendanceTimeline();
    }, [loadAttendanceTimeline]);

    const handleClockIn = async () => {
        if (!attendanceLogs) return;
        try {
            const response = await API.post('/attendance/clock-in');
            const punchTime = response.data?.data?.time || new Date().toLocaleTimeString('en-US', { hour12: false });
            toast.success(`Web Check-In successful at ${punchTime}`);
            loadAttendanceTimeline();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Check-In terminal transaction rejected.');
        }
    };

    const handleClockOut = async () => {
        if (!attendanceLogs) return;
        try {
            await API.post('/attendance/clock-out');
            toast.success(`Web Check-Out verified. Shift segment processed.`);
            loadAttendanceTimeline();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Check-Out terminal exception.');
        }
    };

    const exportToCSV = () => {
        try {
            let csvContent = "data:text/csv;charset=utf-8,Date,Day,Schedule,Clock In,Clock Out,Hours,Status\n";
            monthDays.forEach(day => {
                const log = attendanceMap[day.rawDateString]?.log;
                const status = day.isCompanyWeekOff ? "Week Off" : leavesMap[day.rawDateString] ? "Leave" : log ? "Present" : "Absent";
                const finalClockOut = !log?.clock_out && log && day.rawDateString !== formatTodayIST() ? "21:00:00" : log?.clock_out || '';

                csvContent += `${day.rawDateString},${day.dayName},${day.isCompanyWeekOff ? 'Rest' : '11:00 AM - 08:00 PM'},${log?.clock_in || ''},${finalClockOut},${log?.total_hours || ''},${status}\n`;
            });
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `Timesheet_${MONTHS_LIST[selectedMonth]}_${selectedYear}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success("Timesheet report downloaded successfully.");
        } catch (e) {
            toast.error("Export generation exception.");
        }
    };

    const yearsList = useMemo(() => Array.from({ length: 5 }, (_, i) => currentYear - 2 + i), [currentYear]);

    return (
        <Box sx={{
            width: '100%',
            height: 'auto',
            bgcolor: '#f3f2f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            fontFamily: '"Salesforce Sans", Arial, sans-serif',
            color: '#181818'
        }}>
            <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} closeOnClick />

            {/* Global Header */}
            <Box sx={{
                bgcolor: '#1bae47',
                height: '55px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                color: '#ffffff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                zIndex: 1100
            }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton sx={{ color: '#ffffff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        <Grid3x3 size={20} />
                    </IconButton>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '0.02em', fontSize: '1rem', cursor: 'default' }}>
                        Attendance & TimeSheets
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', display: { xs: 'none', md: 'block' } }}>
                        {liveTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
                    </Typography>
                    <IconButton size="small" sx={{ color: '#ffffff' }}>
                        <HelpCircle size={18} />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#ffffff' }}>
                        <Bell size={18} />
                    </IconButton>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: '#10b981', fontWeight: 700 }}>U</Avatar>
                </Stack>
            </Box>

            {/* Workspace Frame */}
            <Box sx={{ p: 3, flex: 1, maxWidth: '1600px', width: '100%', mx: 'auto', boxSizing: 'border-box' }}>
                <Box sx={{
                    bgcolor: '#ffffff',
                    border: '1px solid #dddbda',
                    borderRadius: '4px',
                    p: 2.5,
                    mb: 3,
                    boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.05)'
                }}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            <Box sx={{ bgcolor: '#4bc076', p: 1.2, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.1)' }}>
                                <Calendar size={32} color="#ffffff" strokeWidth={2.5} />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm sx={{ pl: 1 }}>
                            <Typography variant="caption" sx={{ color: '#444444', fontWeight: 600, display: 'block', fontSize: '0.75rem', lineHeight: 1 }}>
                                Workforce Management
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#081c3b', fontSize: '1.4rem' }}>
                                    Complete Timesheet Record Ledger
                                </Typography>
                                <Chip
                                    size="small"
                                    label={isClockedIn ? "Session Active" : "Logged Out"}
                                    sx={{
                                        bgcolor: isClockedIn ? '#e1f5fe' : '#f3f2f1',
                                        color: isClockedIn ? '#0176d3' : '#747474',
                                        fontWeight: 700,
                                        fontSize: '11px',
                                        height: '20px',
                                        border: `1px solid ${isClockedIn ? '#bfe3f8' : '#dddbda'}`
                                    }}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm="auto">
                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                <ButtonGroup variant="outlined" size="small" sx={{
                                    '& .MuiButton-root': {
                                        borderColor: '#dddbda',
                                        color: '#0176d3',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        px: 2,
                                        '&:hover': { bgcolor: '#f3f2f1', borderColor: '#b0adab' }
                                    }
                                }}>
                                    {!isClockedIn ? (
                                        <Button startIcon={<LogIn size={13} />} onClick={handleClockIn}>Check In</Button>
                                    ) : (
                                        <Button startIcon={<LogOut size={13} />} onClick={handleClockOut} sx={{ color: '#ea001e !important' }}>Check Out</Button>
                                    )}
                                    <Button startIcon={<Download size={13} />} onClick={exportToCSV}>Export Report</Button>
                                    <Button startIcon={<FileText size={13} />}>Regularization</Button>
                                </ButtonGroup>

                                <FormControl size="small" sx={{ minWidth: 110 }}>
                                    <Select
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                        sx={{ fontSize: '12px', fontWeight: 600, height: '30px', borderRadius: '4px' }}
                                    >
                                        {MONTHS_LIST.map((m, idx) => <MenuItem key={m} value={idx}>{m}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 80 }}>
                                    <Select
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                                        sx={{ fontSize: '12px', fontWeight: 600, height: '30px', borderRadius: '4px' }}
                                    >
                                        {yearsList.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid #f3f2f1' }}>
                        <Grid container spacing={1}>
                            <SalesforceMiniField label="Adherence Yield Rate" value={`${metricsSummary.completionRate}%`} progress valueColor="#0176d3" progressVal={metricsSummary.completionRate} />
                            <SalesforceMiniField label="Present Cycles" value={metricsSummary.totalPunches} />
                            <SalesforceMiniField label="Late Check-Ins" value={metricsSummary.lateCount} valueColor={metricsSummary.lateCount > 0 ? '#ff9a00' : 'inherit'} />
                            <SalesforceMiniField label="Approved Leaves" value={metricsSummary.approvedLeaveCount} />
                            <SalesforceMiniField label="Absent Count" value={metricsSummary.absentCount} valueColor={metricsSummary.absentCount > 0 ? '#ea001e' : 'inherit'} />
                        </Grid>
                    </Box>
                </Box>

                {/* Ledger Table Matrix */}
                <Box sx={{
                    bgcolor: '#ffffff',
                    border: '1px solid #dddbda',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.03)',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f3f2f1', borderBottom: '1px solid #dddbda', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <BarChart2 size={16} color="#747474" />
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#081c3b' }}>
                                Ledger Framework: Summary Items ({monthDays.length})
                            </Typography>
                        </Stack>
                        <IconButton size="small" onClick={loadAttendanceTimeline} sx={{ color: '#747474' }}>
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        </IconButton>
                    </Box>

                    <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                        <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { py: 1, px: 2, borderBottom: '1px solid #dddbda', fontSize: '13px' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ bgcolor: '#f3f2f1', fontWeight: 700, fontSize: '11px', color: '#444444', textTransform: 'uppercase' }}>Calendar Track</TableCell>
                                    <TableCell sx={{ bgcolor: '#f3f2f1', fontWeight: 700, fontSize: '11px', color: '#444444', textTransform: 'uppercase' }}>Shift Profile</TableCell>
                                    <TableCell sx={{ bgcolor: '#f3f2f1', fontWeight: 700, fontSize: '11px', color: '#444444', textTransform: 'uppercase' }}>First Scan (In)</TableCell>
                                    <TableCell sx={{ bgcolor: '#f3f2f1', fontWeight: 700, fontSize: '11px', color: '#444444', textTransform: 'uppercase' }}>Last Scan (Out)</TableCell>
                                    <TableCell sx={{ bgcolor: '#f3f2f1', fontWeight: 700, fontSize: '11px', color: '#444444', textTransform: 'uppercase' }} align="center">Active Duration</TableCell>
                                    <TableCell sx={{ bgcolor: '#f3f2f1', fontWeight: 700, fontSize: '11px', color: '#444444', textTransform: 'uppercase' }} align="right">Compliance Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {monthDays.map((day) => {
                                    const dayData = attendanceMap[day.rawDateString];
                                    const log = dayData?.log;
                                    const isLateIn = dayData?.isLateIn || false;
                                    const approvedLeave = leavesMap[day.rawDateString];
                                    const todayStr = formatTodayIST();
                                    const isFutureDate = day.rawDateString > todayStr;
                                    const isPastDate = day.rawDateString < todayStr;
                                    const isMissedOutPunch = log && !log.clock_out && isPastDate;

                                    let statusLabel = "Absent";
                                    let statusColor = "#ea001e";
                                    let statusBg = "#ffebee";

                                    if (day.isCompanyWeekOff) {
                                        statusLabel = "Week Off";
                                        statusColor = "#747474";
                                        statusBg = "#f3f2f1";
                                    } else if (approvedLeave) {
                                        statusLabel = "Leave Approved";
                                        statusColor = "#0176d3";
                                        statusBg = "#e1f5fe";
                                    } else if (log) {
                                        if (isMissedOutPunch) {
                                            statusLabel = "Missed Out Punch";
                                            statusColor = "#d32f2f";
                                            statusBg = "#ffebee";
                                        } else {
                                            statusLabel = isLateIn ? "Late Entry" : "Present";
                                            statusColor = isLateIn ? "#ff9a00" : "#2e7d32";
                                            statusBg = isLateIn ? "#fff3e0" : "#e8f5e9";
                                        }
                                    } else if (isFutureDate) {
                                        statusLabel = "Scheduled";
                                        statusColor = "#444444";
                                        statusBg = "#ffffff";
                                    }

                                    return (
                                        <TableRow key={day.rawDateString} hover sx={{ '&:hover': { bgcolor: '#f3f2f1 !important' } }}>
                                            <TableCell sx={{ fontWeight: 600, color: '#181818' }}>
                                                {String(day.dayNum).padStart(2, '0')} {MONTHS_LIST[selectedMonth].substring(0, 3)}
                                                <Typography component="span" sx={{ color: '#747474', ml: 0.5, fontSize: '12px', fontWeight: 500 }}>({day.dayName})</Typography>
                                            </TableCell>
                                            <TableCell sx={{ color: day.isCompanyWeekOff ? '#747474' : '#181818' }}>
                                                {day.isCompanyWeekOff ? 'Weekly Standard Off' : approvedLeave ? 'Leave Profile' : '11:00 AM — 08:00 PM'}
                                            </TableCell>
                                            <TableCell sx={{ color: isLateIn ? '#ea001e' : '#181818', fontWeight: isLateIn ? 700 : 500 }}>
                                                {log?.clock_in || '—'}
                                            </TableCell>
                                            <TableCell>
                                                {log ? (
                                                    log.clock_out ? (
                                                        log.clock_out
                                                    ) : day.rawDateString === todayStr ? (
                                                        <Typography component="span" sx={{ fontSize: '12px', color: '#2e7d32', fontWeight: 700 }}>Tracking Session Active</Typography>
                                                    ) : (
                                                        <Tooltip title="Missed Out Punch. Automatically defaulted to 09:00 PM." arrow>
                                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: 'inline-flex', color: '#c62828', fontWeight: 700 }}>
                                                                <AlertTriangle size={14} />
                                                                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700 }}>21:00:00</Typography>
                                                            </Stack>
                                                        </Tooltip>
                                                    )
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>
                                                {log?.total_hours || (isMissedOutPunch ? "Auto Calc" : '—')}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box component="span" sx={{
                                                    bgcolor: statusBg,
                                                    color: statusColor,
                                                    px: 1.5,
                                                    py: 0.3,
                                                    borderRadius: '12px',
                                                    fontSize: '11.5px',
                                                    fontWeight: 700,
                                                    display: 'inline-block',
                                                    border: `1px solid ${statusColor}40`
                                                }}>
                                                    {statusLabel}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </Box>
    );
};

const SalesforceMiniField = ({ label, value, valueColor, progress, progressVal }) => (
    <Grid item xs={12} sm={6} md={2.4} sx={{ borderRight: { md: '1px solid #dddbda' }, '&:last-child': { borderRight: 'none' }, px: 2, py: { xs: 1, md: 0 } }}>
        <Typography variant="caption" sx={{ color: '#747474', fontWeight: 500, display: 'block', fontSize: '0.72rem' }}>
            {label}
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: valueColor || '#181818', fontSize: '1.05rem' }}>
                {value}
            </Typography>
            {progress && (
                <LinearProgress
                    variant="determinate"
                    value={progressVal > 100 ? 100 : progressVal}
                    sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#0176d3' } }}
                />
            )}
        </Stack>
    </Grid>
);

export default Attendance;